import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

const CANDIDATE_STATUSES_BLOCKED_FROM_LOGIN = ["completed", "paused", "failed"];

/**
 * Protected Route Component
 * Redirects to login if user is not authenticated
 * Optionally restricts access based on user role
 * Candidates with completed/paused/failed status are signed out and redirected to login
 */
function ProtectedRoute({ children, allowedRoles = null }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [candidateBlocked, setCandidateBlocked] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCandidateBlocked(false);
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const role = userData.role || null;
            setUserRole(role);

            // Candidates with completed, paused, or failed status cannot access the app
            if (role === "candidate" && userData.entityId) {
              const candidateDoc = await getDoc(
                doc(db, "candidates", userData.entityId)
              );
              if (candidateDoc.exists()) {
                const candidateStatus = candidateDoc.data().status;
                if (
                  candidateStatus &&
                  CANDIDATE_STATUSES_BLOCKED_FROM_LOGIN.includes(candidateStatus)
                ) {
                  await signOut(auth);
                  setCandidateBlocked(true);
                  setAuthenticated(false);
                  setUserRole(null);
                  setLoading(false);
                  return;
                }
              }
            }
          } else {
            setUserRole(null);
          }
          setAuthenticated(true);
        } catch (error) {
          console.error("Error fetching user role:", error);
          setUserRole(null);
        }
      } else {
        setAuthenticated(false);
        setUserRole(null);
        setCandidateBlocked(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (candidateBlocked || !authenticated) {
    return (
      <Navigate
        to="/login"
        replace
        state={candidateBlocked ? { candidateAccountInactive: true } : undefined}
      />
    );
  }

  // If allowedRoles is specified, check if user's role is allowed
  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    // Redirect to dashboard if user doesn't have access
    return <Navigate to="/dashboard/lessons" replace />;
  }

  return children;
}

export default ProtectedRoute;
